// @ts-nocheck
import { TextFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import {
    parseBreakpointString,
    validateBreakpointTextField,
} from "@store/Framework/Common/Utilities/breakpointString";
import isValidCssUnit, { defaultValidCssUnits } from "@store/Framework/Common/Utilities/isValidCssUnit";
import { unitToRem } from "@store/Framework/Common/Utilities/unitHelper";
import { GridPreset } from "@toolkit/Framework/Types/Grid";
import { useMemo } from "react";

export type CssUnitFieldValue = string | number | undefined | null;
export interface CssUnitFieldOptions {
    defaultUnit?: string | null;
    isChildDefinition?: boolean;
    shorthands?: string[];
    trim?: boolean;
    validCssUnits?: string[];
}

export const validateCssUnitField = (value: CssUnitFieldValue, options?: CssUnitFieldOptions) => {
    const { defaultUnit, shorthands, trim, validCssUnits } = options || {};
    let _value = typeof value === "number" ? `${value}` : value;

    if (trim) {
        _value = _value?.trim();
    }

    if (!_value) {
        return "";
    }

    let values = [_value];

    if (shorthands) {
        values = _value.split(" ").filter(x => (trim ? x : true));

        if (values.length > shorthands.length) {
            return `Too many values are provided to the shorthand property`;
        }
    }

    for (const x of values) {
        if (!isValidCssUnit(x, validCssUnits, Boolean(defaultUnit))) {
            return `CSS length unit is${defaultUnit ? " " : " missing or "}invalid`;
        }
    }

    return "";
};

export const getCssUnitFieldData = (value: CssUnitFieldValue, options?: CssUnitFieldOptions) => {
    const { defaultUnit, shorthands, trim } = options || {};

    return parseBreakpointString(`${value}`, trim)?.map(parsedValue => {
        if (!parsedValue) {
            return null;
        }

        return parsedValue
            .split(" ")
            .filter(x => x)
            .slice(0, shorthands?.length || 1)
            .map(x => {
                const parsedUnit =
                    x && defaultUnit && !Number.isNaN(Number(x))
                        ? `${Number(x)}${defaultUnit}`
                        : isValidCssUnit(x)
                          ? x
                          : null;

                return parsedUnit ? unitToRem(parsedUnit) : parsedUnit;
            })
            .join(" ");
    });
};

export const getCssUnitFieldDefinition = (name: string, options?: CssUnitFieldOptions) => {
    const { defaultUnit, isChildDefinition, trim, validCssUnits } = options || {};
    const tooltip = `A single or an array (breakpoints) of CSS units.`;

    const definition: TextFieldDefinition = {
        name,
        fieldType: !isChildDefinition ? "General" : (undefined as any),
        editorTemplate: "TextField",
        defaultValue: "",
        validate: (value, item) => validateBreakpointTextField(value, x => validateCssUnitField(x, options), trim),
        tooltip,
    };

    if (validCssUnits?.length) {
        definition.tooltip = `${tooltip} Valid length units: ${validCssUnits
            .map(x => (x === defaultUnit ? `${x} (Default)` : x))
            .join(", ")}`;
    }

    return definition;
};

const getCssUnitField = (name: string, options?: CssUnitFieldOptions) => {
    const _options = { defaultUnit: "px", trim: true, validCssUnits: defaultValidCssUnits, ...options };

    const parse = (value: CssUnitFieldValue) => getCssUnitFieldData(value, _options);
    const parseToGridPreset = (value: CssUnitFieldValue): GridPreset | undefined => {
        const parsedValues = getCssUnitFieldData(value, _options);

        if (!parsedValues) {
            return undefined;
        }

        const vertical: GridPreset["vertical"] = [];
        const horizontal: GridPreset["horizontal"] = [];

        parsedValues.forEach(x => {
            const subValues = x?.split(" ");
            vertical.push(subValues?.[0] || null);
            horizontal.push(subValues?.[1] || subValues?.[0] || null);
        });

        return {
            horizontal,
            vertical,
        };
    };

    return {
        definition: getCssUnitFieldDefinition(name, _options),
        parse,
        useParse: (...parameters: Parameters<typeof parse>) => {
            return useMemo(() => parse(...parameters), parameters);
        },
        parseToGridPreset,
        useParseToGridPreset: (...parameters: Parameters<typeof parseToGridPreset>) => {
            return useMemo(() => parseToGridPreset(...parameters), parameters);
        },
    };
};

export default getCssUnitField;
